import { modal } from 'components/Modals/ModalManager';
import { ShareableRoutineFolder } from 'hevy-shared';
import { makeAutoObservable } from 'mobx';
import toast from 'react-hot-toast';
import { stores } from 'state/stores';
import API from 'utils/API';
import { fireAndForget } from 'utils/async';
import { sendEvent } from 'utils/events';
import { log } from 'utils/log';
import { copyToClipboard } from 'utils/clipboard';
import { t } from 'translations/t';

interface Props {
  folderId: number | null;
}

export class FolderDetailViewModel {
  folder?: ShareableRoutineFolder;
  isError: boolean = false;
  isFolderSaved: boolean = false;
  isSavingFolder: boolean = false;
  isInitialLoading: boolean = true;

  constructor({ folderId }: Props) {
    log('FolderDetailViewModel constructor');

    makeAutoObservable(this);

    if (folderId !== null) {
      this.fetch(folderId);
    } else {
      this.isError = true;
    }
  }

  get isAuthenticated() {
    return stores.auth.isAuthenticated;
  }

  get isFolderEmpty() {
    return this.folder?.routines.length === 0;
  }

  get isUsersFolder() {
    if (stores.auth.isAuthenticated && this.folder) {
      const folder = stores.routineFolders.getFolderById(this.folder.id);
      return !!folder;
    }

    return false;
  }

  private fetch = async (folderId: number) => {
    try {
      const result = await API.getShareableFolderById(folderId);
      this.folder = result.data;
      this.isError = false;
    } catch {
      this.isError = true;
    } finally {
      this.isInitialLoading = false;
    }
  };

  onSaveFolder = async () => {
    if (!this.folder) return;

    const routineLength = stores.routines.routines.length + this.folder.routines.length;

    if (!stores.subscription.isPro && routineLength >= 4) {
      modal.paywall({
        type: 'routine-limit',
        onCancel: () => null,
      });
      return;
    }

    const save = async (folder: ShareableRoutineFolder) => {
      try {
        this.isSavingFolder = true;
        const response = await API.postRoutineFolder({ title: folder.title });
        const routines = folder.routines.map(r =>
          API.postRoutineCopy({
            routineId: r.id,
            ignoreValues: true,
            index: r.index,
            folderId: response.data.id,
          }),
        );
        await Promise.all(routines);
        await fireAndForget([stores.routines.fetch()]);
        this.isSavingFolder = false;
        this.isFolderSaved = true;
        toast.success(t('web.folderDetail.saveFolder.success'));
      } catch (e) {
        toast.error(t('web.folderDetail.saveFolder.error'));
      }
    };

    if (this.isFolderSaved || this.isUsersFolder) {
      modal.confirmation({
        title: t('web.folderDetail.saveFolderAgain'),
        subtitle: t('web.folderDetail.saveFolderAgain.subtitle'),
        submitButtonTitle: t('web.folderDetail.saveFolderAgain.submitButton'),
        onConfirm: () => save(this.folder!),
      });
    } else {
      save(this.folder);
    }
  };

  onCopyUrl = () => {
    if (!this.folder) return;
    sendEvent('folderDetail_copyLink', { folderId: this.folder.id });
    copyToClipboard(window.location.href);
    toast.success(t('global.copiedToClipboard'));
  };

  get title(): string {
    return this.folder?.title || '';
  }

  get username(): string {
    return this.folder?.username || '';
  }

  get profilePic(): string {
    return this.folder?.profile_pic || '';
  }

  get existingUserSaveButtonTitle(): string {
    return this.isFolderSaved
      ? t('web.folderDetail.saveButton.saved')
      : t('web.folderDetail.saveButton.saveFolder');
  }
}
